import { useContext, useMemo, useEffect, useCallback, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import restApi from '../lib/restApi';
import getRoute from '../utils/getRoute';
// eslint-disable-next-line import/no-cycle
import { Context } from '../context/AppContext';

const useContextApi = ssr => {
  const { pathname, search } = useLocation();
  const params = useParams();
  const route = getRoute(pathname);
  const query = useMemo(() => queryString.parse(search || ''), [search]);

  const {
    setContextData = () => {},
    isServer,
    cookie,
    userAgent: { userAgent } = {},
    ...context
  } = useContext(Context) || {};

  const apiArray = useMemo(
    () =>
      ssr({
        params,
        query,
        pathname,
        cookies: cookie.getCookieAll(),
        setContext: () => {},
        redirect: () => {},
        userAgent,
      }) || [],
    [params, query, cookie, userAgent, ssr]
  );

  const [prevOptions, setPrevOptions] = useState(
    (apiArray || []).map(() => ({
      params: {},
    }))
  );

  const defaults = (apiArray || []).reduce(
    (acc, { api }) => ({
      ...acc,
      [api]: {
        loading: false,
        error: false,
      },
    }),
    {}
  );

  const routeData = (context[route] && context[route].data) || defaults;

  const setApiData = useCallback(
    (api, data) => {
      setContextData(route, {
        ...context[route],
        data: {
          ...routeData,
          [api]: {
            ...routeData[api],
            ...data,
          },
        },
      });
    },
    [context, route, routeData, setContextData]
  );

  const routeDataWithFetch = useMemo(() => {
    const reFetch = (api, options, callback = () => {}) => {
      const setData = async data => {
        const callbackData = await callback(data);
        setApiData(api, callbackData || data);
      };

      const setLoading = () => {
        setApiData(api, { loading: true });
      };

      const setError = status => {
        setApiData(api, {
          error: status,
          loading: false,
        });
      };

      setLoading();

      const fetchAsync = async () => {
        await restApi(api, options, setData, () => {}, setError);
      };
      fetchAsync();
    };

    return Object.keys(routeData).reduce(
      (acc, key, index) => ({
        ...acc,
        [key]: {
          ...routeData[key],
          fetch: options =>
            reFetch(
              key,
              options || apiArray[index].options,
              apiArray[index].callback
            ),
        },
      }),
      {}
    );
  }, [routeData, setApiData, apiArray]);

  const objectCompare = (obj, compare) =>
    Object.keys(obj).some(key => obj[key] !== compare[key]);

  const getOptions = useCallback(
    () => (apiArray || []).map(item => item.options),
    [apiArray]
  );

  useEffect(() => {
    if (isServer) {
      setPrevOptions(getOptions());
      return;
    }

    apiArray.forEach(({ options, api }, index) => {
      const hasChanged = objectCompare(
        options.params,
        prevOptions[index].params
      );
      if (hasChanged && routeDataWithFetch[api]) {
        routeDataWithFetch[api].fetch();
        setPrevOptions(getOptions());
      }
    });
  }, [
    apiArray,
    isServer,
    prevOptions,
    routeDataWithFetch,
    setContextData,
    getOptions,
  ]);

  return routeDataWithFetch;
};

export default useContextApi;
